import config from "config";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { API_URL, createAxiosInstance } from "../../../api";
import "./ProfileStepInformationStatuses.css";
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton, 
  Stack,
  Divider,
  Tooltip,
  Alert
} from "@mui/material";
import {
  Book as BookIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  ContactMail as ContactIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  RadioButtonUnchecked as OptionalIcon,
  Info as InfoIcon,
  Circle as CircleIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Error as ErrorIcon
} from "@mui/icons-material";

const ProfileStepInformationStatuses = ({ onSubjectsStatusChange, onBillingStatusChange }) => {
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState("false"); // Local state for billing status
  const [subjectsCom, setSubjectsCom] = useState(false); // Use state to track subject completion

  // Get profile data from Redux
  const {
    avatar,
    about,
    price,
    qualifications,
    title,
    online,
    faceToFace,
    gender,
    isAgency,
    links,
    publish,
    email,
    phone,
    exam,
    level,
    _id,
    status,
  } = useSelector((state) => state.profile.user);

  const userId = useSelector((state) => state.main.userId);

  const checkUserSubjects = useCallback(async () => {
    if (!userId) {
      console.log("User ID is undefined or invalid. Skipping subject check.");
      return; // Exit the function early if the userId is not valid
    }

    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER}/api/subjects/user/${userId}`
      );

      const subCompleted = response.data || [];
      if (subCompleted.length > 0) {
        // There is at least one entry in the array
        setSubjectsCom(true); // Set subjects completion to true in state
        onSubjectsStatusChange && onSubjectsStatusChange(true);
      } else {
        setSubjectsCom(false); // Set subjects completion to false in state
        onSubjectsStatusChange && onSubjectsStatusChange(false);
        console.log("No entries found");
      }
    } catch (error) {
      console.error(
        "Error checking subjects status:",
        error.response?.data?.message || error.message
      );
      setSubjectsCom(false);
      onSubjectsStatusChange && onSubjectsStatusChange(false);
    }
  }, [userId, onSubjectsStatusChange]);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!userId) {
        console.log("User ID is undefined or invalid. Skipping payment check.");
        return; // Exit the function early if the userId is not valid
      }

      try {

        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        // Handle string values "Paid" and "Not Paid" from the API
        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;
        
        if (response.data && isPaid) {
          setBillingStatus(response.data.paid);
          onBillingStatusChange && onBillingStatusChange(true);
        } else {
          setBillingStatus("false");
          onBillingStatusChange && onBillingStatusChange(false);
        }
      } catch (error) {
        console.error(
          "Error checking payment status:",
          error.response?.data?.message || error.message
        );
        setBillingStatus("false");
        onBillingStatusChange && onBillingStatusChange(false);
      }
    };

    checkPaymentStatus();
    checkUserSubjects();
  }, [userId, onBillingStatusChange, checkUserSubjects]); // Re-run the effect when `userId` changes

  // Helper function to get tooltip text for incomplete steps
  const getTooltipText = (step) => {
    switch (step) {
      case 'subjects':
        return 'Add at least one subject you can teach to complete this step.';
      case 'tutoring-options':
        return 'Select whether you offer online, face-to-face, or both tutoring options.';
      case 'profile':
        return 'Complete your profile with title, gender, about section, and profile photo.';
      case 'qualifications':
        return 'Add your educational qualifications and certifications.';
      case 'contact':
        return 'Optional: Add your contact information for students to reach you.';
      case 'payment':
        return 'Complete your subscription payment to make your profile live.';
      default:
        return 'Complete this step to continue with your profile setup.';
    }
  };

  // Helper function to create step items
  const createStepItem = (step, title, icon, isCompleted, isOptional = false, stepId, isLast = false) => (
    <Box key={step} sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          gap: { xs: 1, sm: 1.5 },
          '&:hover': {
            backgroundColor: 'rgba(113, 45, 148, 0.04)',
            transform: 'translateX(4px)',
            boxShadow: '0 2px 8px rgba(113, 45, 148, 0.1)'
          }
        }}
        onClick={() => navigate(`/profile?step=${stepId}`)}
      >
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 1.5 }, flex: 1, minWidth: 0 }}>
        <Box 
          className="profile-step-icon-container"
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            minWidth: { xs: 36, sm: 40 },
            borderRadius: '50%',
            backgroundColor: isCompleted ? 'rgba(36, 161, 72, 0.1)' : isOptional ? 'rgba(158, 158, 158, 0.1)' : 'rgba(113, 45, 148, 0.1)',
            color: isCompleted ? '#24A148' : isOptional ? '#9e9e9e' : '#712d94',
            border: isCompleted ? '2px solid #24A148' : isOptional ? '2px solid #9e9e9e' : '2px solid #712d94',
            flexShrink: 0,
            mt: { xs: 0.25, sm: 0 },
            '& svg': {
              fontSize: { xs: '18px', sm: '20px' },
              width: { xs: '18px !important', sm: '20px !important' },
              height: { xs: '18px !important', sm: '20px !important' },
              minWidth: { xs: '18px !important', sm: '20px !important' },
              minHeight: { xs: '18px !important', sm: '20px !important' },
              flexShrink: 0
            }
          }}
        >
          {React.cloneElement(icon, { className: 'profile-step-icon' })}
        </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ 
              fontWeight: 600, 
              color: '#333',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.4,
              wordBreak: 'break-word'
            }}>
              {title}
            </Typography>
            {isOptional && (
              <Typography variant="body2" color="text.secondary" sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mt: 0.25
              }}>
                Optional
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {isCompleted ? (
            <Chip
              icon={<CheckIcon />}
              label="Completed"
              color="success"
              size="small"
              variant="filled"
              sx={{ 
                fontWeight: 600,
                '& .MuiChip-label': {
                  display: { xs: 'none', sm: 'inline' },
                  px: 0
                },
                '& .MuiChip-icon': {
                  m: { xs: 0, sm: '0 6px 0 -4px' }
                },
                minWidth: { xs: '32px', sm: 'auto' },
                px: { xs: '4px', sm: '10px' }
              }}
            />
          ) : isOptional ? (
            <Chip
              icon={<InfoIcon />}
              label="Optional"
              color="default"
              size="small"
              variant="outlined"
              sx={{ 
                fontWeight: 600,
                '& .MuiChip-label': {
                  display: { xs: 'none', sm: 'inline' },
                  px: 0
                },
                '& .MuiChip-icon': {
                  m: { xs: 0, sm: '0 6px 0 -4px' }
                },
                minWidth: { xs: '32px', sm: 'auto' },
                px: { xs: '4px', sm: '10px' }
              }}
            />
          ) : (
            <Tooltip title={getTooltipText(step)} arrow placement="top">
              <Chip
                icon={<WarningIcon sx={{ color: '#d32f2f' }} />}
                label="Incomplete"
                size="small"
                variant="outlined"
                sx={{ 
                  fontWeight: 600, 
                  cursor: 'help',
                  backgroundColor: 'white',
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  '& .MuiChip-label': {
                    display: { xs: 'none', sm: 'inline' },
                    px: 0
                  },
                  '& .MuiChip-icon': {
                    m: { xs: 0, sm: '0 6px 0 -4px' }
                  },
                  minWidth: { xs: '32px', sm: 'auto' },
                  px: { xs: '4px', sm: '10px' },
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    borderColor: '#b71c1c'
                  }
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
      
      {/* Visual connector line */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '37px', sm: '39px' }, // Center of the icon
            top: { xs: '52px', sm: '56px' }, // Below the step item
            width: '2px',
            height: { xs: '14px', sm: '16px' },
            backgroundColor: isCompleted ? '#24A148' : '#e0e0e0',
            zIndex: 1
          }}
        />
      )}
    </Box>
  );

  // Create steps array for better control
  const steps = [
    {
      key: 'subjects',
      title: config["profile-step-0-title"],
      icon: <BookIcon />,
      isCompleted: subjectsCom,
      isOptional: false,
      stepId: 'subjects'
    },
    {
      key: 'tutoring-options',
      title: 'Select your tutoring options',
      icon: <SettingsIcon />,
      isCompleted: online || faceToFace,
      isOptional: false,
      stepId: 'tutoring-options'
    },
    {
      key: 'profile',
      title: 'Create your tutor profile',
      icon: <PersonIcon />,
      isCompleted: title && gender !== -1 && about && about !== "<p><br></p>" && avatar,
      isOptional: false,
      stepId: 'tutor-profile'
    },
    {
      key: 'qualifications',
      title: 'Tell us about your qualifications',
      icon: <SchoolIcon />,
      isCompleted: Object.keys(qualifications).some((k) => qualifications[k] !== -1),
      isOptional: false,
      stepId: 'qualifications'
    },
    {
      key: 'contact',
      title: 'How do clients get in touch with you?',
      icon: <ContactIcon />,
      isCompleted: email || phone,
      isOptional: true,
      stepId: 'contact-info'
    }
  ];

  // Add payment step (always show if profile exists)
  if (_id) {
    steps.push({
      key: 'payment',
      title: 'Make payment for subscription',
      icon: <PaymentIcon />,
      isCompleted: billingStatus === "Paid" || billingStatus === true,
      isOptional: false,
      stepId: 'subscription'
    });
  }

  return (
    <Box>
      <Stack spacing={0}>
        {steps.map((step, index) => 
          createStepItem(
            step.key,
            step.title,
            step.icon,
            step.isCompleted,
            step.isOptional,
            step.stepId,
            index === steps.length - 1 // isLast
          )
        )}
      </Stack>
    </Box>
  );
};

export default ProfileStepInformationStatuses;
